import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import PaymentsRoundedIcon from "@mui/icons-material/PaymentsRounded";
import SavingsRoundedIcon from "@mui/icons-material/SavingsRounded";
import WebProfitSpinner from "../../system/WebProfitSpinner";
import { erpApi } from "../../../api/erp";
import { ERP_BRAND, entityId, formatMoney } from "../../../utils/erp";
import { hasProjectPermission } from "../../../utils/projectAccess";
import { ErpPanel } from "./ErpUi";

const PROFIT = "#159455";
const LOSS = "#C83A3A";
const COST = "#F08A3E";
const BLUE = "#2F80ED";

const EMPTY_EXPENSE = {
  name: "",
  amount: "",
  frequency: "monthly",
  dueDayFrom: "1",
  dueDayTo: "10",
  cashRegisterId: "",
  expenseAccountCode: "OTHER_EXPENSE",
  notes: "",
};

function frequencyLabel() {
  return "Mensual";
}

function dueLabel(expense) {
  const from = Number(expense?.dueDayFrom || 1);
  const to = Number(expense?.dueDayTo || from);
  return from === to ? `día ${from}` : `entre el ${from} y el ${to}`;
}

function safeMoney(value, currency) {
  return value === null || value === undefined ? "—" : formatMoney(value, currency);
}

function TargetBlock({ eyebrow, daily, weekly, monthly, currency, tone = PROFIT, detail }) {
  return (
    <Box sx={{ minWidth: 0, p: { xs: 1.25, sm: 1.55 }, borderRadius: 2.7, bgcolor: "rgba(var(--wp-panel-ink-rgb,25,36,47),.025)", border: `1px solid ${ERP_BRAND.border}` }}>
      <Typography variant="caption" sx={{ color: ERP_BRAND.muted, fontWeight: 900 }}>{eyebrow}</Typography>
      <Stack direction="row" spacing={1.2} alignItems="baseline" sx={{ mt: 0.25, minWidth: 0 }}>
        <Typography noWrap sx={{ minWidth: 0, color: tone, fontWeight: 1000, fontSize: { xs: "1.28rem", sm: "1.65rem" }, letterSpacing: "-.025em" }}>
          {safeMoney(daily, currency)}
        </Typography>
        <Typography variant="caption" sx={{ color: ERP_BRAND.muted, fontWeight: 850 }}>/ día</Typography>
      </Stack>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={{ xs: 0.15, sm: 1.15 }} sx={{ mt: 0.25 }}>
        <Typography variant="caption" sx={{ color: ERP_BRAND.dark, fontWeight: 850 }}>{safeMoney(weekly, currency)} / semana</Typography>
        <Typography variant="caption" sx={{ color: ERP_BRAND.muted }}>{safeMoney(monthly, currency)} / mes</Typography>
      </Stack>
      {detail ? <Typography variant="caption" sx={{ display: "block", color: ERP_BRAND.muted, mt: 0.55, lineHeight: 1.3 }}>{detail}</Typography> : null}
    </Box>
  );
}

export default function FutureEconomyBoard({ projectId, access, bootstrap = {}, navigate, movementsHref, currency = "ARS" }) {
  const canManage = hasProjectPermission(access, "erp.expenses.create") && hasProjectPermission(access, "erp.ledger.read") && hasProjectPermission(access, "financial.totals.read");
  const [economy, setEconomy] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const [expenseDialog, setExpenseDialog] = React.useState(null);
  const [expenseForm, setExpenseForm] = React.useState(EMPTY_EXPENSE);
  const [settingsOpen, setSettingsOpen] = React.useState(false);
  const [manageOpen, setManageOpen] = React.useState(false);
  const [settingsForm, setSettingsForm] = React.useState({ targetMonthlyProfit: "0", businessDaysPerWeek: "6" });

  const load = React.useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const data = await erpApi.futureEconomy(projectId, { currency });
      setEconomy(data?.economy || null);
    } catch (requestError) {
      setError(requestError?.message || "No se pudo calcular la economía futura.");
    } finally {
      setLoading(false);
    }
  }, [currency, projectId]);

  React.useEffect(() => { load(); }, [load]);

  const registers = React.useMemo(
    () => (bootstrap?.registers || []).filter((row) => row?.active !== false && String(row.currency || "ARS").toUpperCase() === String(currency).toUpperCase()),
    [bootstrap?.registers, currency],
  );
  const expenseAccounts = React.useMemo(
    () => (bootstrap?.accounts || []).filter((row) => row?.active !== false && row?.type === "expense" && row?.metadata?.source !== "cash_register"),
    [bootstrap?.accounts],
  );
  const registerById = React.useMemo(() => new Map(registers.map((row) => [entityId(row), row])), [registers]);

  const openExpense = (expense = null) => {
    setExpenseDialog(expense || { id: "" });
    const defaultExpenseAccount = expenseAccounts.find((account) => account.code === "OTHER_EXPENSE")?.code
      || expenseAccounts[0]?.code
      || "OTHER_EXPENSE";
    setExpenseForm(expense ? {
      name: expense.name || "",
      amount: String(expense.amount || ""),
      frequency: expense.frequency || "monthly",
      dueDayFrom: String(expense.dueDayFrom || 1),
      dueDayTo: String(expense.dueDayTo || expense.dueDayFrom || 10),
      cashRegisterId: expense.cashRegisterId || "",
      expenseAccountCode: expense.expenseAccountCode || "OTHER_EXPENSE",
      notes: expense.notes || "",
    } : { ...EMPTY_EXPENSE, expenseAccountCode: defaultExpenseAccount });
    setError("");
  };

  const saveExpense = async () => {
    try {
      setSaving(true);
      setError("");
      const payload = {
        ...expenseForm,
        amount: Number(expenseForm.amount || 0),
        dueDayFrom: Number(expenseForm.dueDayFrom || 1),
        dueDayTo: Number(expenseForm.dueDayTo || expenseForm.dueDayFrom || 1),
        currency,
      };
      const data = expenseDialog?.id
        ? await erpApi.updateFixedExpense(projectId, expenseDialog.id, payload)
        : await erpApi.createFixedExpense(projectId, payload);
      setEconomy(data?.economy || economy);
      setExpenseDialog(null);
    } catch (requestError) {
      setError(requestError?.message || "No se pudo guardar el gasto fijo.");
    } finally {
      setSaving(false);
    }
  };

  const archiveExpense = async (expense) => {
    if (!window.confirm(`¿Quitar “${expense.name}” de tus gastos fijos? Los movimientos ya registrados no se modifican.`)) return;
    try {
      setSaving(true);
      const data = await erpApi.deleteFixedExpense(projectId, expense.id);
      setEconomy(data?.economy || economy);
      setExpenseDialog(null);
    } catch (requestError) {
      setError(requestError?.message || "No se pudo quitar el gasto fijo.");
    } finally {
      setSaving(false);
    }
  };

  const openSettings = () => {
    setSettingsForm({
      targetMonthlyProfit: String(economy?.settings?.targetMonthlyProfit || 0),
      businessDaysPerWeek: String(economy?.settings?.businessDaysPerWeek || 6),
    });
    setSettingsOpen(true);
    setError("");
  };

  const saveSettings = async () => {
    try {
      setSaving(true);
      setError("");
      const data = await erpApi.updateFutureEconomySettings(projectId, {
        targetMonthlyProfit: Number(settingsForm.targetMonthlyProfit || 0),
        businessDaysPerWeek: Number(settingsForm.businessDaysPerWeek || 6),
        lookbackDays: economy?.settings?.lookbackDays || 90,
        currency,
      });
      setEconomy(data?.economy || economy);
      setSettingsOpen(false);
    } catch (requestError) {
      setError(requestError?.message || "No se pudo guardar el objetivo.");
    } finally {
      setSaving(false);
    }
  };

  const registerExpenseNow = (expense) => {
    if (!movementsHref) return;
    const params = new URLSearchParams({
      nuevo: "1",
      tipo: "expense",
      origen: "gasto-fijo",
      concepto: expense.name || "Gasto fijo",
      monto: String(expense.amount || 0),
      cuenta: expense.expenseAccountCode || "OTHER_EXPENSE",
      nota: `Gasto fijo · ${frequencyLabel(expense.frequency)} · previsto ${dueLabel(expense)}`,
    });
    if (expense.cashRegisterId && registerById.has(String(expense.cashRegisterId))) params.set("caja", expense.cashRegisterId);
    navigate(`${movementsHref}?${params.toString()}`);
  };

  if (loading && !economy) {
    return <ErpPanel><Box sx={{ minHeight: 290, display: "grid", placeItems: "center" }}><WebProfitSpinner size={28} /></Box></ErpPanel>;
  }

  if (!economy) {
    return <Alert severity="error" action={<Button color="inherit" onClick={load}>Reintentar</Button>}>{error || "No se pudo cargar la economía futura."}</Alert>;
  }

  const target = economy.targets?.desiredProfit || {};
  const breakEven = economy.targets?.breakEven || {};
  const pace = economy.targets?.currentPace || {};
  const assumptions = economy.assumptions || {};
  const fixedExpenses = economy.fixedExpenses || [];
  const warnings = economy.confidence?.warnings || [];
  const primaryWarning = warnings[0] || null;
  const warningSeverity = primaryWarning?.severity === "error" ? "error" : primaryWarning?.severity === "info" ? "info" : "warning";
  const needsFixedExpenseReview = warnings.some((row) => ["missing_fixed_expenses", "fixed_expenses_may_be_incomplete"].includes(row.code));
  const desiredProfit = Number(economy.totals?.targetMonthlyProfit || 0);
  const targetConfigured = desiredProfit > 0;
  const pacePercent = targetConfigured ? pace.againstDesiredPercent : pace.againstBreakEvenPercent;
  const againstTarget = Number(pacePercent || 0);
  const paceTone = pacePercent === null || pacePercent === undefined
    ? ERP_BRAND.muted
    : againstTarget >= 100 ? PROFIT : LOSS;

  return (
    <Stack spacing={0.85}>
      {error ? <Alert severity="error" onClose={() => setError("")} sx={{ borderRadius: 2.5 }}>{error}</Alert> : null}

      <ErpPanel sx={{ overflow: "hidden" }}>
        <Box sx={{ p: { xs: 1.15, sm: 1.45 } }}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={1.2} justifyContent="space-between" alignItems={{ md: "center" }}>
            <Box sx={{ minWidth: 0 }}>
              <Stack direction="row" spacing={0.8} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography sx={{ color: ERP_BRAND.dark, fontWeight: 1000, fontSize: { xs: "1rem", sm: "1.12rem" } }}>Economía futura</Typography>
                <Chip size="small" icon={<SavingsRoundedIcon />} label="Meta de ventas" sx={{ height: 24, fontWeight: 900, bgcolor: "rgba(21,148,85,.09)", color: PROFIT }} />
              </Stack>
              <Typography variant="caption" sx={{ display: "block", color: ERP_BRAND.muted, mt: 0.25 }}>
                Lo que deberías vender para cubrir tu estructura y generar ganancia, usando el margen real que viene registrando el negocio.
              </Typography>
            </Box>
            {canManage ? (
              <Stack direction="row" spacing={0.7} sx={{ flexShrink: 0 }}>
                <Button size="small" variant="outlined" onClick={openSettings} sx={{ minHeight: 34, textTransform: "none", fontWeight: 900 }}>Ajustar meta</Button>
                <Button size="small" variant="contained" startIcon={<AddRoundedIcon />} onClick={() => openExpense()} sx={{ minHeight: 34, textTransform: "none", fontWeight: 900 }}>Gasto fijo</Button>
              </Stack>
            ) : null}
          </Stack>

          <Box sx={{ mt: 1, display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0,1fr)) minmax(230px,.72fr)" }, gap: 0.8 }}>
            <TargetBlock
              eyebrow="PARA SOSTENER EL NEGOCIO"
              daily={breakEven.daily}
              weekly={breakEven.weekly}
              monthly={breakEven.monthly}
              currency={currency}
              tone={BLUE}
              detail="Punto de equilibrio estimado: cubre costos directos y gastos fijos declarados."
            />
            <TargetBlock
              eyebrow={desiredProfit > 0 ? `PARA GANAR ${formatMoney(desiredProfit, currency)} AL MES` : "TU GANANCIA OBJETIVO"}
              daily={targetConfigured ? target.dailySales : null}
              weekly={targetConfigured ? target.weeklySales : null}
              monthly={targetConfigured ? target.monthlySales : null}
              currency={currency}
              tone={PROFIT}
              detail={desiredProfit > 0 ? "Incluye la ganancia mensual que elegiste." : "Definí una ganancia mensual para convertirla en una meta concreta."}
            />
            <Box sx={{ p: 1.25, borderRadius: 2.7, bgcolor: "rgba(var(--wp-panel-ink-rgb,25,36,47),.025)", border: `1px solid ${ERP_BRAND.border}` }}>
              <Typography variant="caption" sx={{ color: ERP_BRAND.muted, fontWeight: 900 }}>RITMO RECIENTE</Typography>
              <Typography sx={{ mt: 0.25, color: paceTone, fontWeight: 1000, fontSize: "1.08rem" }}>{safeMoney(pace.dailySales, currency)} / día</Typography>
              <Typography variant="caption" sx={{ display: "block", color: ERP_BRAND.muted, mt: 0.2 }}>
                {pacePercent === null || pacePercent === undefined
                  ? "Todavía falta historial para comparar."
                  : targetConfigured
                    ? `Vas al ${Math.round(againstTarget)}% de tu meta con ganancia.`
                    : `Vas al ${Math.round(againstTarget)}% del punto de equilibrio.`}
              </Typography>
              {pacePercent !== null && pacePercent !== undefined ? (
                <Box sx={{ mt: 0.75, height: 6, borderRadius: 99, bgcolor: "rgba(var(--wp-panel-ink-rgb,25,36,47),.08)", overflow: "hidden" }}>
                  <Box sx={{ width: `${Math.min(100, Math.max(0, againstTarget))}%`, height: "100%", borderRadius: 99, bgcolor: paceTone, transition: "width .2s ease" }} />
                </Box>
              ) : null}
            </Box>
          </Box>

          <Box sx={{ mt: 0.8, display: "grid", gridTemplateColumns: { xs: "repeat(2,minmax(0,1fr))", sm: "repeat(4,minmax(0,1fr))" }, border: `1px solid ${ERP_BRAND.border}`, borderRadius: 2.2, overflow: "hidden" }}>
            {[
              ["Gastos fijos", formatMoney(economy.totals?.fixedExpensesMonthly || 0, currency)],
              ["Margen bruto", assumptions.grossMarginRate === null || assumptions.grossMarginRate === undefined ? "Sin base" : `${Number(assumptions.grossMarginRate).toLocaleString("es-AR", { maximumFractionDigits: 1 })}%`],
              ["Compromisos", `${fixedExpenses.length} cargados`],
              ["Días abiertos", `${economy.settings?.businessDaysPerWeek || 6} por semana`],
            ].map(([label, value], index) => (
              <Box key={label} sx={{ px: 1, py: 0.72, minWidth: 0, borderRight: { xs: index % 2 === 0 ? `1px solid ${ERP_BRAND.border}` : 0, sm: index < 3 ? `1px solid ${ERP_BRAND.border}` : 0 }, borderBottom: { xs: index < 2 ? `1px solid ${ERP_BRAND.border}` : 0, sm: 0 } }}>
                <Typography noWrap variant="caption" sx={{ display: "block", color: ERP_BRAND.muted, fontWeight: 850 }}>{label}</Typography>
                <Typography noWrap variant="caption" sx={{ display: "block", color: ERP_BRAND.dark, fontWeight: 950, mt: 0.08 }}>{value}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </ErpPanel>

      {primaryWarning ? (
        <Alert severity={warningSeverity} sx={{ borderRadius: 2.5, py: 0.25 }}>
          <Typography variant="caption" sx={{ fontWeight: 950 }}>{primaryWarning.title}</Typography>
          <Typography variant="caption" sx={{ display: "block", mt: 0.15 }}>{primaryWarning.message}</Typography>
          {canManage && needsFixedExpenseReview ? <Button size="small" onClick={() => openExpense()} sx={{ mt: 0.35, p: 0, minWidth: 0, textTransform: "none", fontWeight: 950 }}>Revisar gastos fijos →</Button> : null}
        </Alert>
      ) : null}

      <ErpPanel>
        <Box sx={{ px: 1.15, py: 0.8, borderBottom: `1px solid ${ERP_BRAND.border}` }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ color: ERP_BRAND.dark, fontWeight: 950, fontSize: ".88rem" }}>Gastos que se repiten</Typography>
              <Typography variant="caption" sx={{ color: ERP_BRAND.muted }}>Alquiler, servicios, sueldos y otros pagos previsibles. Si la meta te parece baja, revisá esta lista.</Typography>
            </Box>
            {canManage && fixedExpenses.length ? <Button size="small" startIcon={<AddRoundedIcon />} onClick={() => openExpense()} sx={{ textTransform: "none", fontWeight: 900 }}>Agregar</Button> : null}
          </Stack>
        </Box>
        {!fixedExpenses.length ? (
          <Box sx={{ px: 1.15, py: 1.05 }}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={0.8} justifyContent="space-between" alignItems={{ sm: "center" }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ color: ERP_BRAND.dark, fontWeight: 900, fontSize: ".8rem" }}>Todavía no cargaste alquiler, luz, internet, sueldos u otros compromisos.</Typography>
                <Typography variant="caption" sx={{ color: ERP_BRAND.muted }}>Sin esos datos, el objetivo de ventas puede quedar artificialmente bajo.</Typography>
              </Box>
              {canManage ? <Button variant="contained" size="small" startIcon={<AddRoundedIcon />} onClick={() => openExpense()} sx={{ flexShrink: 0, textTransform: "none", fontWeight: 900 }}>Agregar primero</Button> : null}
            </Stack>
          </Box>
        ) : (
          <Box>
            {fixedExpenses.slice(0, 4).map((expense, index) => {
              const register = registerById.get(String(expense.cashRegisterId || ""));
              return (
                <Box key={expense.id} sx={{ px: 1.15, py: 0.78, borderTop: index ? `1px solid ${ERP_BRAND.border}` : 0 }}>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={{ xs: 0.55, sm: 1 }} alignItems={{ sm: "center" }} justifyContent="space-between">
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Stack direction="row" spacing={0.7} alignItems="center" sx={{ minWidth: 0 }}>
                        <Typography noWrap sx={{ minWidth: 0, color: ERP_BRAND.dark, fontWeight: 900, fontSize: ".8rem" }}>{expense.name}</Typography>
                        <Chip size="small" label={frequencyLabel(expense.frequency)} variant="outlined" sx={{ height: 20, fontSize: ".62rem", fontWeight: 850 }} />
                      </Stack>
                      <Typography noWrap variant="caption" sx={{ display: "block", color: ERP_BRAND.muted, mt: 0.12 }}>
                        Previsto {dueLabel(expense)}{register?.name ? ` · sale de ${register.name}` : " · caja a definir"}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={0.55} alignItems="center" sx={{ flexShrink: 0 }}>
                      <Typography sx={{ mr: 0.35, color: COST, fontWeight: 950, fontSize: ".8rem" }}>{formatMoney(expense.amount, currency)}</Typography>
                      {movementsHref && canManage ? <Button size="small" startIcon={<PaymentsRoundedIcon />} onClick={() => registerExpenseNow(expense)} sx={{ minHeight: 30, textTransform: "none", fontWeight: 900 }}>Registrar</Button> : null}
                      {canManage ? <Button size="small" startIcon={<EditRoundedIcon />} onClick={() => openExpense(expense)} sx={{ minHeight: 30, textTransform: "none", fontWeight: 900 }}>Editar</Button> : null}
                    </Stack>
                  </Stack>
                </Box>
              );
            })}
            {fixedExpenses.length > 4 ? (
              <Box sx={{ px: 1.15, py: 0.62, borderTop: `1px solid ${ERP_BRAND.border}` }}>
                <Button size="small" onClick={() => setManageOpen(true)} sx={{ p: 0, minWidth: 0, textTransform: "none", fontWeight: 900 }}>Ver los {fixedExpenses.length} gastos fijos →</Button>
              </Box>
            ) : null}
          </Box>
        )}
      </ErpPanel>

      <Dialog open={Boolean(expenseDialog)} onClose={saving ? undefined : () => setExpenseDialog(null)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 950 }}>{expenseDialog?.id ? "Editar gasto fijo" : "Nuevo gasto fijo"}</DialogTitle>
        <Divider />
        <DialogContent>
          <Stack spacing={1.15} sx={{ pt: 0.5 }}>
            <TextField autoFocus fullWidth label="¿Qué gasto se repite?" placeholder="Ej.: Alquiler del local" value={expenseForm.name} onChange={(event) => setExpenseForm((current) => ({ ...current, name: event.target.value }))} />
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1.15fr .85fr" }, gap: 1 }}>
              <TextField fullWidth type="number" label="Importe aproximado" value={expenseForm.amount} onChange={(event) => setExpenseForm((current) => ({ ...current, amount: event.target.value }))} inputProps={{ min: 0, step: "0.01" }} />
              <TextField fullWidth label="Periodicidad" value="Todos los meses" disabled />
            </Box>
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2,minmax(0,1fr))", gap: 1 }}>
              <TextField type="number" label="Desde el día" value={expenseForm.dueDayFrom} onChange={(event) => setExpenseForm((current) => ({ ...current, dueDayFrom: event.target.value }))} inputProps={{ min: 1, max: 31 }} />
              <TextField type="number" label="Hasta el día" value={expenseForm.dueDayTo} onChange={(event) => setExpenseForm((current) => ({ ...current, dueDayTo: event.target.value }))} inputProps={{ min: 1, max: 31 }} />
            </Box>
            <TextField select fullWidth label="¿De qué caja o cuenta suele salir?" value={expenseForm.cashRegisterId} onChange={(event) => setExpenseForm((current) => ({ ...current, cashRegisterId: event.target.value }))} helperText="Podés dejarlo sin definir y elegir la caja cuando registres el pago.">
              <MenuItem value="">Definir al pagarlo</MenuItem>
              {registers.map((register) => <MenuItem key={entityId(register)} value={entityId(register)}>{register.name}</MenuItem>)}
            </TextField>
            <TextField select fullWidth label="Cuenta contable" value={expenseForm.expenseAccountCode} onChange={(event) => setExpenseForm((current) => ({ ...current, expenseAccountCode: event.target.value }))}>
              {expenseAccounts.length ? expenseAccounts.map((account) => <MenuItem key={account.code} value={account.code}>{account.name || account.code}</MenuItem>) : <MenuItem value="OTHER_EXPENSE">Otros gastos</MenuItem>}
            </TextField>
            <TextField fullWidth label="Nota opcional" value={expenseForm.notes} onChange={(event) => setExpenseForm((current) => ({ ...current, notes: event.target.value }))} />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0.5 }}>
          {expenseDialog?.id ? <Button color="error" onClick={() => archiveExpense(expenseDialog)} disabled={saving} sx={{ mr: "auto", textTransform: "none", fontWeight: 850 }}>Quitar</Button> : null}
          <Button onClick={() => setExpenseDialog(null)} disabled={saving} sx={{ textTransform: "none", fontWeight: 850 }}>Cancelar</Button>
          <Button variant="contained" onClick={saveExpense} disabled={saving || !expenseForm.name.trim() || Number(expenseForm.amount || 0) <= 0} startIcon={saving ? <WebProfitSpinner size={15} /> : undefined} sx={{ textTransform: "none", fontWeight: 950 }}>
            {saving ? "Guardando..." : "Guardar gasto fijo"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={manageOpen} onClose={() => setManageOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 950 }}>Todos los gastos fijos</DialogTitle>
        <Divider />
        <DialogContent sx={{ p: 0 }}>
          {fixedExpenses.map((expense, index) => {
            const register = registerById.get(String(expense.cashRegisterId || ""));
            return (
              <Box key={expense.id} sx={{ px: 1.5, py: 1, borderTop: index ? `1px solid ${ERP_BRAND.border}` : 0 }}>
                <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
                  <Box sx={{ minWidth: 0 }}>
                    <Typography noWrap sx={{ color: ERP_BRAND.dark, fontWeight: 900, fontSize: ".82rem" }}>{expense.name}</Typography>
                    <Typography noWrap variant="caption" sx={{ color: ERP_BRAND.muted }}>{formatMoney(expense.amount, currency)} · {dueLabel(expense)}{register?.name ? ` · ${register.name}` : ""}</Typography>
                  </Box>
                  {canManage ? <Button size="small" startIcon={<EditRoundedIcon />} onClick={() => { setManageOpen(false); openExpense(expense); }} sx={{ flexShrink: 0, textTransform: "none", fontWeight: 900 }}>Editar</Button> : null}
                </Stack>
              </Box>
            );
          })}
        </DialogContent>
        <DialogActions sx={{ p: 1.5 }}>
          <Button onClick={() => setManageOpen(false)} sx={{ textTransform: "none", fontWeight: 900 }}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={settingsOpen} onClose={saving ? undefined : () => setSettingsOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 950 }}>Objetivo económico</DialogTitle>
        <Divider />
        <DialogContent>
          <Stack spacing={1.2} sx={{ pt: 0.5 }}>
            <Typography variant="body2" sx={{ color: ERP_BRAND.muted }}>Decile a WebProfit cuánto querés que quede de ganancia por mes. Lo convierte a ventas semanales y diarias usando tu margen real.</Typography>
            <TextField autoFocus fullWidth type="number" label="Ganancia mensual deseada" value={settingsForm.targetMonthlyProfit} onChange={(event) => setSettingsForm((current) => ({ ...current, targetMonthlyProfit: event.target.value }))} inputProps={{ min: 0, step: "0.01" }} />
            <TextField select fullWidth label="Días que abrís por semana" value={settingsForm.businessDaysPerWeek} onChange={(event) => setSettingsForm((current) => ({ ...current, businessDaysPerWeek: event.target.value }))}>
              {[1,2,3,4,5,6,7].map((day) => <MenuItem key={day} value={String(day)}>{day} {day === 1 ? "día" : "días"}</MenuItem>)}
            </TextField>
            <Alert severity="info" sx={{ borderRadius: 2.2 }}>La proyección usa los últimos {economy?.settings?.lookbackDays || 90} días de ventas y costos para estimar el margen bruto. No reemplaza una planificación financiera detallada.</Alert>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0.5 }}>
          <Button onClick={() => setSettingsOpen(false)} disabled={saving} sx={{ textTransform: "none", fontWeight: 850 }}>Cancelar</Button>
          <Button variant="contained" onClick={saveSettings} disabled={saving} startIcon={saving ? <WebProfitSpinner size={15} /> : <ArrowForwardRoundedIcon />} sx={{ textTransform: "none", fontWeight: 950 }}>Guardar objetivo</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
